import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { CBMError, Column, DatatableButton, Siterecord } from 'app/models';
import {
  ConfigurationStatusType,
  FcdEncodingStatusType,
  LocationOverviewType,
  StorageKey,
  VildEncodingStatusType
} from 'app/models/enums';
import { SiterecordService, StorageService } from 'app/services';
import { CbmTableComponent } from 'app/shared/datatable/cbm-table.component';
import { IntendedUsageUtil } from 'app/utils/intended-usage.util';

@Component({
  selector: 'admin-location-overview',
  templateUrl: './admin-locations-overview.component.html',
  styleUrls: ['./admin-locations-overview.component.scss']
})
export class AdminLocationsOverviewComponent implements OnInit {
  @ViewChild(CbmTableComponent) datatable: CbmTableComponent<Siterecord>;

  public showTable = true;

  public columns: Column[] = [
    { property: 'id', label: 'LOCATIONS.ID', type: 'string', filterable: true, flexGrow: 1.5 },
    { property: 'recordId', label: 'LOCATIONS.RECORDID', type: 'string', filterable: true },
    { property: 'recordName', label: 'LOCATIONS.RECORDNAME', type: 'string', filterable: true },
    {
      property: 'recordVersionNr',
      label: 'LOCATIONS.RECORDVERSIONNUMBER',
      type: 'string',
      filterable: true,
      flexGrow: 1
    },
    { property: 'recordVersionDateTime', label: 'LOCATIONS.RECORDVERSIONDATETIME', type: 'date' },
    { property: 'locationType', label: 'LOCATIONS.LOCATIONTYPE', type: 'string', filterable: true },
    { property: 'status', label: 'LOCATIONS.STATUS', type: 'string', filterable: true, flexGrow: 1.5 },
    { property: 'vildEncodingStatus', label: 'LOCATIONS.VILDENCODINGSTATUS', type: 'string', filterable: true },
    { property: 'fcdEncodingStatus', label: 'LOCATIONS.FCDENCODINGSTATUS', type: 'string', filterable: true },
    { property: 'active', label: 'LOCATIONS.ACTIVE', type: 'string', filterable: true, flexGrow: 1.5 },
    { property: 'inShapefile', label: 'LOCATIONS.INSHAPEFILE', type: 'string', filterable: true, flexGrow: 1.5 },
    { property: 'mutationDateTime', label: 'LOCATIONS.MUTATIONDATETIME', type: 'date', flexGrow: 2.2 },
    { property: 'contractId', label: 'LOCATIONS.CONTRACTID', type: 'string', filterable: true },
    {
      property: 'intendedUsageType',
      label: 'LOCATIONS.INTENDEDUSAGE',
      type: 'translatable',
      valueCallback: this.parseIntendedUsageType.bind(this),
      filterable: true,
      flexGrow: 1.5
    }
  ];

  public buttons: DatatableButton[] = [
    {
      id: 'pencil',
      action: this.goToLocationEditPage.bind(this),
      showIf: AdminLocationsOverviewComponent.showEditButton.bind(this),
      icon: 'pencil',
      classes: 'btn btn-primary'
    }
  ];

  public siterecordData: Siterecord[] = [];
  public deletedSiterecordData: Siterecord[] = [];

  public isAllLocationsActive = true;
  public isVildErrorsActive = false;
  public isFcdErrorsActive = false;
  public isValidationErrorsActive = false;
  public isDeletedSiderecordsActive = false;

  public loading = false;

  private readonly FILTER_VILDERRORS = 'vildErrors';
  private readonly FILTER_FCDERRORS = 'fcdErrors';
  private readonly FILTER_VALIDATIONERRORS = 'validationErrors';
  private readonly FILTER_DELETED = 'deleted';

  private static showEditButton(siterecord: Siterecord): boolean {
    return !siterecord.deleted;
  }

  constructor(
    private readonly router: Router,
    private readonly siterecordService: SiterecordService,
    private readonly storageService: StorageService
  ) {}

  ngOnInit() {
    const locationMode = localStorage.getItem(StorageKey.LOCATION_MODE);
    if (locationMode === 'map') {
      this.showTable = false;
    }

    this.getAllSiterecords();
  }

  private setFilters(): void {
    const filter = localStorage.getItem(StorageKey.ADMIN_LOCATION_FILTER);
    if (filter === this.FILTER_VILDERRORS) {
      this.filterOnVildErrors();
    } else if (filter === this.FILTER_FCDERRORS) {
      this.filterOnFcdErrors();
    } else if (filter === this.FILTER_VALIDATIONERRORS) {
      this.filterOnRequiredValidations();
    } else if (filter === this.FILTER_DELETED) {
      this.filterOnDeleted();
    }
  }

  private getDeletedSiteRecords(): void {
    if (this.deletedSiterecordData.length > 0) {
      return;
    }

    this.loading = true;
    this.siterecordService.getDeletedSiterecords().subscribe(
      (siterecords: Siterecord[]) => {
        this.deletedSiterecordData = siterecords ? siterecords : [];
        this.loading = false;
      },
      (errors: CBMError[]) => {
        console.log('Something went wrong getting deleted siterecords:', errors);
        this.loading = false;
      }
    );
  }

  private getAllSiterecords(): void {
    this.loading = true;
    this.siterecordService.getAllSiterecords().subscribe(
      (siterecords: Siterecord[]) => {
        this.siterecordData = siterecords ? siterecords : [];
        if (this.showTable) {
          this.setFilters();
        }

        this.loading = false;
      },
      (errors: CBMError[]) => {
        console.log('Something went wrong getting all siterecords:', errors);
        this.loading = false;
      }
    );
  }

  public amountOfVildErrors(): number {
    const errorStatuses = [VildEncodingStatusType.ERROR, VildEncodingStatusType.AREA];
    return this.siterecordData.filter(sr => errorStatuses.indexOf(sr.vildEncodingStatus) !== -1).length;
  }

  public amountOfFcdErrors(): number {
    const errorStatuses = [FcdEncodingStatusType.ERROR, FcdEncodingStatusType.NOTROUTABLE];
    return this.siterecordData.filter(sr => errorStatuses.indexOf(sr.fcdEncodingStatus) !== -1).length;
  }

  public amountOfValidationsErrors(): number {
    const errorStatuses = [
      ConfigurationStatusType.AUTO_VALIDATED_CORRECT,
      ConfigurationStatusType.AUTO_VALIDATED_ERROR,
      ConfigurationStatusType.ERROR
    ];
    return this.siterecordData.filter(sr => errorStatuses.indexOf(sr.status) !== -1).length;
  }

  public amountOfLocations() {
    return this.siterecordData.length;
  }

  public filterOnVildErrors(): void {
    const statusRegex = `${VildEncodingStatusType.ERROR}|${VildEncodingStatusType.AREA}`;
    this.datatable.setRegexFilter('vildEncodingStatus', statusRegex);
    this.setIsActive(false, true, false, false, false);
    localStorage.setItem(StorageKey.ADMIN_LOCATION_FILTER, this.FILTER_VILDERRORS);
  }

  public filterOnFcdErrors(): void {
    const statusRegex = `${FcdEncodingStatusType.ERROR}|${FcdEncodingStatusType.NOTROUTABLE}`;
    this.datatable.setRegexFilter('fcdEncodingStatus', statusRegex);
    this.setIsActive(false, false, true, false, false);
    localStorage.setItem(StorageKey.ADMIN_LOCATION_FILTER, this.FILTER_FCDERRORS);
  }

  public filterOnRequiredValidations(): void {
    const statusRegex = `${ConfigurationStatusType.AUTO_VALIDATED_CORRECT}|${ConfigurationStatusType.AUTO_VALIDATED_ERROR}|${ConfigurationStatusType.ERROR}`;
    this.datatable.setRegexFilter('status', statusRegex);
    this.setIsActive(false, false, false, true, false);
    localStorage.setItem(StorageKey.ADMIN_LOCATION_FILTER, this.FILTER_VALIDATIONERRORS);
  }

  public filterOnDeleted(): void {
    this.setIsActive(false, false, false, false, true);
    this.getDeletedSiteRecords();
    localStorage.setItem(StorageKey.ADMIN_LOCATION_FILTER, this.FILTER_DELETED);
  }

  public filterReset(): void {
    this.datatable.resetRegexFilter();
    this.setIsActive(true, false, false, false, false);
    localStorage.removeItem(StorageKey.ADMIN_LOCATION_FILTER);
  }

  private setIsActive(
    allLocations: boolean,
    vildErrors: boolean,
    fcdErrors: boolean,
    validationErros: boolean,
    deletedRecords: boolean
  ): void {
    this.isAllLocationsActive = allLocations;
    this.isVildErrorsActive = vildErrors;
    this.isFcdErrorsActive = fcdErrors;
    this.isValidationErrorsActive = validationErros;
    this.isDeletedSiderecordsActive = deletedRecords;
  }

  public clickShowTable(): void {
    this.showTable = true;
    localStorage.setItem(StorageKey.LOCATION_MODE, 'table');
  }

  public clickShowMap(): void {
    this.showTable = false;
    localStorage.setItem(StorageKey.LOCATION_MODE, 'map');
  }

  public goToLocationdetailPage(location: Siterecord) {
    if (this.datatable) {
      this.storageService.setLocationListInStorage(
        this.datatable.getListOfPropertiesOfDisplayedData(['id', 'locationType'])
      );
      this.storageService.setLocationOverviewOrigin(LocationOverviewType.LOCATION_OVERVIEW_ADMIN);
    }
    this.router.navigate([`/location/${location.locationType}/detail/${location.id}`]);
  }

  public goToLocationEditPage(location: Siterecord) {
    if (this.datatable) {
      this.storageService.setLocationListInStorage(
        this.datatable.getListOfPropertiesOfDisplayedData(['id', 'locationType'])
      );
      this.storageService.setLocationOverviewOrigin(LocationOverviewType.LOCATION_OVERVIEW_ADMIN);
    }
    this.router.navigate([`/location/${location.locationType}/edit/${location.id}`]);
  }

  public parseIntendedUsageType(location: Siterecord): string {
    return IntendedUsageUtil.parseIntendedUsageTypeAsString(location);
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/locations/admin-locations-overview.component.ts